import { payEquityAuditDraft } from './draft';
import { payEquityAuditEmployments } from './employments';
import { getApiHost } from '@/appSettings';
import { RestManager } from '@/newApi/services/rest/RestManager';
import type { Collection} from '@/newApi/services/ResgateManager';
import { ResErr, ResgateManager } from '@/newApi/services/ResgateManager';
import type { InputFieldNames } from '@sysarb/sysarb-types-v2/services/v1/analytics';
import type { AdjustedSalaryFactor, ArchivedAdjustedSalaryFactorSnapshot, CreateAdjustedSalaryFactorInput, EmployeeImportData, ExtendedBlinderOaxacaGroup, PayEquityAudit, PayEquityAuditMathsSummary, PayFactorStatisticsResponse, Work } from '@sysarb/sysarb-types-v2/services/v1/pay-equity-audit';
import type { DateFormat, Error as SalError } from '@sysarb/sysarb-types-v2/services/v1/import';
import { keepPreviousData, queryOptions, type UseQueryOptions } from '@tanstack/vue-query';
import type { Guid } from 'guid-typescript';
import { MappingContainer } from '@/models/common/MappingContainer';
import { useLoginStore } from '@/login/stores/useLoginStore';
import type { Role } from '@sysarb/sysarb-types/service-base/enums';
import { useMockSalaryFactors } from '@/mocks/mockSalaryFactors';
import { toValue, type MaybeRefOrGetter } from 'vue';

const { salaryFactors, addMockSalaryFactor, removeMockSalaryFactor, generateMockSalaryFactorAnalysis } = useMockSalaryFactors();

const mock = {
    salaryFactorAnalysis: false,
    salaryFactors: false,
};

/**
 * Deterministic business rejection from the analysis service — retrying yields
 * the same result, so the retry predicate skips it. Transport faults stay as a
 * plain Error and keep retrying.
 */
class AnalysisBusinessError extends Error {
    public constructor(message?: string, options?: ErrorOptions) {
        super(message, options);
        this.name = 'AnalysisBusinessError';
    }
}

const retryUnlessBusinessError = (failureCount: number, error: Error): boolean =>
    !(error instanceof AnalysisBusinessError) && failureCount < 3;

 const restManager = new RestManager(`https://${getApiHost()}/pay-equity-audit-service`);

type ImportEmployeeDataInput = {
    payEquityAuditId: string;
    file: File;
    column: InputFieldNames;
    dateFormat: DateFormat;
};

const employeeImport = {
    queries: {
        getEmployeeImportData: (payEquityAuditId: string | null | undefined, field?: InputFieldNames | null | undefined): UseQueryOptions<EmployeeImportData[] | null, Error> => {
            const { instance } = useLoginStore();
            const inputValid = payEquityAuditId != null && field != null;
            return queryOptions<EmployeeImportData[] | null, Error>({
                queryKey: ['employeeImportData', instance, payEquityAuditId, field],
                enabled: () => inputValid,
                queryFn: async (): Promise<EmployeeImportData[] | null> => {
                    try {
                         return await restManager.get<EmployeeImportData[]>(`v1/${instance}/pay-equity-audit/${payEquityAuditId}/employeeImportData?field=${field}`);
                    } catch (error) {
                        if (error instanceof Error) {
                            throw error;
                        } else {
                            throw new Error('An unknown error occurred while fetching employee import data.');
                        }
                    }
                }
            });
        }
    },
    mutations: {
        importEmployeeData: {
            mutationFn: async (input: ImportEmployeeDataInput): Promise<{ success: true } | { success: false; error: SalError }> => {
                const { instance } = useLoginStore();
                const form = new FormData();
                form.append('file', input.file, 'file');
                form.append('column', input.column);
                form.append('dateformat', input.dateFormat.toString());
                try {
                    return await restManager.post<{ success: true } | { success: false; error: SalError }>(form, `v1/${instance}/pay-equity-audit/${input.payEquityAuditId}/importEmployeeData`,
                        {
                            'x-subdomain': instance ?? '',
                            'Content-type': 'multipart/form-data'
                        }
                    );
                } catch (error) {
                    if (error instanceof Error) {
                        throw error;
                    } else {
                        throw new Error('An unknown error occurred while importing employee data.');
                    }
                }
            }
        }
    }
};

const factorAnalysis = {
    queries: {
                /**
         * Query for fetching model insights analysis data (ExtendedBlinderOaxacaGroup).
         * Uses the FPI v1 model endpoint.
         * @deprecated
         * For fetching extended analysis data based on selected salary factors, use getSalaryFactorAnalysis instead, which uses the FPI v2 model endpoint and returns more detailed insights.
         */
        getFactorAnalysis: (payEquityAuditId: string | null | undefined, salaryFactorCount: number, enabled: MaybeRefOrGetter<boolean> = true): UseQueryOptions<ExtendedBlinderOaxacaGroup | null, Error> => {
            const { instanceTopic, userId, role } = useLoginStore();

            const roleValue: Role | undefined = role?.role;
            const inputValid = payEquityAuditId != null && salaryFactorCount > 0;
            return queryOptions<ExtendedBlinderOaxacaGroup | null, Error>({
                queryKey: [
                    'modelInsights',
                    'analysis',
                    userId,
                    roleValue,
                    instanceTopic,
                    payEquityAuditId,
                    salaryFactorCount
            ],
                enabled: () => toValue(enabled) && inputValid,
                retry: retryUnlessBusinessError,
                queryFn: async (): Promise<ExtendedBlinderOaxacaGroup | null> => {
                    const topic = `v1.${instanceTopic}.${userId?.toLowerCase() ?? ''}.${roleValue}.pay-equity-audit.${payEquityAuditId}`;

                    const res = await ResgateManager.getInstance().call<ExtendedBlinderOaxacaGroup>(topic, 'adjustedpaygap', {});

                    if (res instanceof ResErr) {
                        throw new Error(res.message);
                    }

                    return res;
                }
            });
        },
        getSalaryFactors: (payEquityAuditId: string | null | undefined): UseQueryOptions<Collection<AdjustedSalaryFactor> | null, Error, AdjustedSalaryFactor[]> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.pay-equity-audit.${payEquityAuditId}.adjusted-salary-factors`;

            return queryOptions<Collection<AdjustedSalaryFactor> | null, Error, AdjustedSalaryFactor[]>({
                queryKey: ['modelInsights', 'salaryFactors', topic, payEquityAuditId],
                enabled: () => payEquityAuditId != null,
                meta: {
                    subscribe: true,
                    type: 'resgate',
                    resourceId: topic,
                },
                placeholderData: keepPreviousData,
                queryFn: async (): Promise<Collection<AdjustedSalaryFactor> | null> => {
                    if (payEquityAuditId == null) return null;
                    if (mock.salaryFactors) {
                        return salaryFactors.value;
                    }
                    const res = await ResgateManager.getInstance().collection<AdjustedSalaryFactor>(topic);
                    if (res instanceof ResErr) {
                        throw new Error(res.message);
                    }
                    return res;
                },
                select: (res): AdjustedSalaryFactor[] => res?.list ?? [],
            });
        },
        getArchivedSalaryFactors: (payEquityAuditId: string | null | undefined): UseQueryOptions<ArchivedAdjustedSalaryFactorSnapshot[] | null, Error> => {
            const { instanceTopic } = useLoginStore();
            const topic = `v1.${instanceTopic}.pay-equity-audit.${payEquityAuditId}.adjusted-salary-factors`;

            return queryOptions<ArchivedAdjustedSalaryFactorSnapshot[] | null, Error>({
                queryKey: ['modelInsights', 'archivedSalaryFactors', topic, payEquityAuditId],
                enabled: () => payEquityAuditId != null && payEquityAuditId !== '',
                queryFn: async (): Promise<ArchivedAdjustedSalaryFactorSnapshot[] | null> => {
                    if (payEquityAuditId == null || payEquityAuditId === '') return null;
                    const res = await ResgateManager.getInstance().call<ArchivedAdjustedSalaryFactorSnapshot[]>(topic, 'getarchived', {});
                    if (res instanceof ResErr) {
                        throw new Error(res.message);
                    }
                    return res;
                },
            });
        },
        /**
         *  Query for fetching salary factor analysis data (ExtendedBlinderOaxacaGroup) based on selected salary factors.
         *  Uses the FPI v2 model endpoint, which returns extended analysis data for the selected factors.
         * @param payEquityAuditId - ID of the pay equity audit for which to fetch salary factor analysis
         * @param salaryFactors  - List of selected salary factors to include in the analysis
         * @returns UseQueryOptions for fetching PayEquityAuditMathsSummary or null if payEquityAuditId is not provided
         */
        getSalaryFactorAnalysis: (payEquityAuditId: string | null | undefined, salaryFactors: AdjustedSalaryFactor[]): UseQueryOptions<PayEquityAuditMathsSummary | null, Error> => {
            const { instanceTopic, userData, role } = useLoginStore();
            const userLoginId = userData?.loginUser?.Id;
            const roleValue: Role | undefined = role?.role;
            return queryOptions<PayEquityAuditMathsSummary | null, Error>({
                queryKey: ['modelInsights', 'v2', instanceTopic, userLoginId, roleValue, payEquityAuditId, salaryFactors],
                enabled: () => payEquityAuditId != null,
                retry: retryUnlessBusinessError,
                placeholderData: keepPreviousData,
                queryFn: async (): Promise<PayEquityAuditMathsSummary | null> => {
                    if (payEquityAuditId == null) return null;
                    if (mock.salaryFactorAnalysis) {
                        await new Promise(resolve => setTimeout(resolve, 1200));
                        const result = generateMockSalaryFactorAnalysis(salaryFactors);
                        if (result.success === false) throw new AnalysisBusinessError(result.errorCode);
                        return result;
                    }
                    const topic = `v1.${instanceTopic}.${userLoginId}.${roleValue}.pay-equity-audit.${payEquityAuditId}`;

                    const res = await ResgateManager.getInstance().call<PayEquityAuditMathsSummary>(topic, 'adjustedpaygapv2', {});
                    if (res instanceof ResErr) {
                        // A reasonId marks a deterministic backend rejection (e.g. NoFactors);
                        // a transport fault has none and should keep retrying.
                        if (res.data?.reasonId != null && res.data.reasonId !== '') throw new AnalysisBusinessError(res.data?.details, { cause: res.data?.reasonId });
                        throw new Error(res.data?.details ?? res.message);
                    }
                    if (res.success === false) throw new AnalysisBusinessError(res.errorCode);
                    return res;
                }
            });
        },
        getDraftFactorStatistics: (draftId: string | null | undefined): UseQueryOptions<PayFactorStatisticsResponse | null, Error> => {
            const { instanceTopic, userData, role } = useLoginStore();
            const userId = userData?.loginUser?.Id.toLowerCase();
            return queryOptions<PayFactorStatisticsResponse | null, Error>({
                queryKey: ['payEquityAudit', 'draftFactorStatistics', instanceTopic, userId, role?.role, draftId],
                enabled: () => draftId != null && draftId !== '',
                queryFn: async (): Promise<PayFactorStatisticsResponse | null> => {
                    if (draftId == null || draftId === '') return null;
                    const topic = `v1.${instanceTopic}.${userId}.${role?.role}.pay-equity-audit-draft.${draftId}`;
                    const res = await ResgateManager.getInstance().call<PayFactorStatisticsResponse>(topic, 'getstatistics', {});
                    if (res instanceof ResErr) throw new Error(res.message);
                    return res;
                }
            });
        },
    },
    mutations: {
        createAdjustedSalaryFactor: {
            mutationFn: async (input: { payEquityAuditId: string; data: CreateAdjustedSalaryFactorInput }): Promise<boolean> => {
                if (mock.salaryFactors) {
                    addMockSalaryFactor(input.data);
                    return true;
                }
                const { instanceTopic } = useLoginStore();
                const res = await ResgateManager.getInstance().call<void>(
                    `v1.${instanceTopic}.pay-equity-audit.${input.payEquityAuditId}.adjusted-salary-factors`,
                    'create',
                    input.data
                );
                if (res instanceof ResErr) {
                    throw new Error(res.message);
                }
                return true;
            }
        },
        removeAdjustedSalaryFactor: {
            mutationFn: async (input: { payEquityAuditId: string; adjustedSalaryFactorId: string }): Promise<boolean> => {
                if (mock.salaryFactors) {
                    removeMockSalaryFactor(input.adjustedSalaryFactorId);
                    return true;
                }
                const { instanceTopic } = useLoginStore();
                const res = await ResgateManager.getInstance().call<void>(
                    `v1.${instanceTopic}.pay-equity-audit.${input.payEquityAuditId}.adjusted-salary-factor.${input.adjustedSalaryFactorId}`,
                    'remove',
                    {}
                );
                if (res instanceof ResErr) {
                    throw new Error(res.message);
                }
                return true;
            }
        }
    }
};

const works = {
    queries: {
        /**
         * Live collection of the audit's works.
         * @param {string | null | undefined} payEquityAuditId - The audit to read works for.
         * @returns {UseQueryOptions<Collection<Work> | null, Error, Work[]>} Query options.
         */
        getWorks: (payEquityAuditId: string | null | undefined): UseQueryOptions<Collection<Work> | null, Error, Work[]> => {
            const { instanceTopic, currentOrganisationNodeId, role } = useLoginStore();
            const auditId = payEquityAuditId?.toLowerCase() ?? '';
            const organisationNodeId = currentOrganisationNodeId?.toLowerCase() ?? '';
            const roleValue = role?.role.toString().toLowerCase() ?? '';

            const topic = `v1.${instanceTopic}.pay-equity-audit.${auditId}.${organisationNodeId}.${roleValue}.works`;
            const isValid = instanceTopic != null && auditId !== '' && organisationNodeId !== '' && roleValue !== '';

            return queryOptions<Collection<Work> | null, Error, Work[]>({
                queryKey: ['payEquityAuditWorks', 'all', topic],
                enabled: () => isValid,
                meta: {
                    subscribe: true,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryFn: async (): Promise<Collection<Work> | null> => {
                    const res = await ResgateManager.getInstance().collection<Work>(topic);
                    if (res instanceof ResErr) {
                        throw new Error(res.message);
                    }
                    return res;
                },
                select: (res): Work[] => res?.list ?? [],
            });
        },
        /**
         * Live collection of the audit's works that have no employments.
         * @param {string | null | undefined} payEquityAuditId - The audit to read empty works for.
         * @returns {UseQueryOptions<Collection<Work> | null, Error, Work[]>} Query options.
         */
        getEmptyWorks: (payEquityAuditId: string | null | undefined): UseQueryOptions<Collection<Work> | null, Error, Work[]> => {
            const { instanceTopic, currentOrganisationNodeId, role } = useLoginStore();
            const auditId = payEquityAuditId?.toLowerCase() ?? '';
            const organisationNodeId = currentOrganisationNodeId?.toLowerCase() ?? '';
            const roleValue = role?.role.toString().toLowerCase() ?? '';

            const emptyWorksTopic = `v1.${instanceTopic}.pay-equity-audit.${auditId}.${organisationNodeId}.${roleValue}.works?emptyEmployments=true`;
            const isValid = instanceTopic != null && auditId !== '' && organisationNodeId !== '' && roleValue !== '';

            return queryOptions<Collection<Work> | null, Error, Work[]>({
                queryKey: ['payEquityAuditWorks', 'empty', emptyWorksTopic],
                enabled: () => isValid,
                meta: {
                    subscribe: true,
                    type: 'resgate',
                    resourceId: emptyWorksTopic,
                },
                queryFn: async (): Promise<Collection<Work> | null> => {
                    const res = await ResgateManager.getInstance().collection<Work>(emptyWorksTopic);
                    if (res instanceof ResErr) {
                        throw new Error(res.message);
                    }
                    return res;
                },
                select: (res): Work[] => res?.list ?? [],
            });
        },
    },
    // NOTE: neither mutation invalidates the works queries. Those are subscribed Resgate
    // collections, so the server publishes the change and `TanstackCache.ts` applies it to the
    // cached collection — an invalidate would only add a redundant round trip.
    mutations: {
        /** Deletes the given works. */
        deleteWorks: {
            mutationFn: async ({ payEquityAuditId, ids }: { payEquityAuditId: string; ids: Guid[] }): Promise<boolean> => {
                if (ids.length === 0) throw new Error('No work ids provided');

                const { instanceTopic, currentOrganisationNodeId, role } = useLoginStore();
                const auditId = payEquityAuditId.toLowerCase();
                const organisationNodeId = currentOrganisationNodeId?.toLowerCase() ?? '';
                const roleValue = role?.role.toString().toLowerCase() ?? '';

                if (instanceTopic == null || auditId === '' || organisationNodeId === '' || roleValue === '') {
                    throw new Error('Audit id, organisation node id and user role are required');
                }

                const topic = `v1.${instanceTopic}.pay-equity-audit.${auditId}.${organisationNodeId}.${roleValue}.works`;
                const res = await ResgateManager.getInstance().call<boolean>(topic, 'delete', ids);
                if (res instanceof ResErr) {
                    throw new Error(res.message);
                }

                MappingContainer.deleteFromStore('mappingWork', ids, true);
                return true;
            },
        },
        /** Deletes every work in the audit that has no employments. */
        deleteEmptyWorks: {
            mutationFn: async ({ payEquityAuditId, ids }: { payEquityAuditId: string; ids: Guid[] }): Promise<boolean> => {
                const { instanceTopic, currentOrganisationNodeId, role } = useLoginStore();
                const auditId = payEquityAuditId.toLowerCase();
                const organisationNodeId = currentOrganisationNodeId?.toLowerCase() ?? '';
                const roleValue = role?.role.toString().toLowerCase() ?? '';

                if (instanceTopic == null || auditId === '' || organisationNodeId === '' || roleValue === '') {
                    throw new Error('Audit id, organisation node id and user role are required');
                }

                const topic = `v1.${instanceTopic}.pay-equity-audit.${auditId}.${organisationNodeId}.${roleValue}.works`;
                const res = await ResgateManager.getInstance().call<boolean>(topic, 'delete-no-employments', {});
                if (res instanceof ResErr) {
                    throw new Error(res.message);
                }

                MappingContainer.deleteFromStore('mappingWork', ids, true);
                return true;
            },
        },
    },
};

const audit = {
    queries: {
        getPayEquityAudit: (payEquityAuditId: string | null | undefined): UseQueryOptions<PayEquityAudit | null, Error> => {
            const { instanceTopic, userId, role } = useLoginStore();
            const roleValue: Role | undefined = role?.role;
            const topic = `v1.${instanceTopic}.${userId?.toLowerCase() ?? ''}.${roleValue}.pay-equity-audit.${payEquityAuditId}`;
            return queryOptions<PayEquityAudit | null, Error>({
                queryKey: ['payEquityAudit', instanceTopic, userId, roleValue, payEquityAuditId, topic],
                enabled: () => payEquityAuditId != null,
                meta: {
                    subscribe: true,
                    type: 'resgate',
                    resourceId: topic,
                },
                queryFn: async (): Promise<PayEquityAudit | null> => {
                    if (payEquityAuditId == null) return null;
                    const res = await ResgateManager.model<PayEquityAudit>(topic);
                    if (res instanceof ResErr) {
                        throw new Error(res.message);
                    }
                    return res as PayEquityAudit;
                }
            });
        }
    }
};

export const payEquityAudit = {
    audit,
    draft: payEquityAuditDraft,
    employeeImport,
    factorAnalysis,
    employments: payEquityAuditEmployments,
    works
};
